/**
 * Browser side of agent import/export: file download, clipboard, file read.
 *
 * The format — the agent plus every procedure it uses — lives in `@erp/rpc`
 * (`agent-bundle.contract.ts`) and is built server-side by `exportBundle`, since
 * collecting the procedures needs more than one `get` response carries.
 */

import { parseAgentBundle, type AgentBundle } from '@erp/rpc';
import {
  downloadJsonText,
  parseJsonText,
  readJsonFile,
  summarizeValidationErrors,
} from 'src/lib/readJsonFile';

export function agentBundleJson(bundle: AgentBundle): string {
  return JSON.stringify(bundle, null, 2);
}

/** `<name>.v<version>.agent.json` — the version suffix names the file, it is not in the bundle. */
export function agentExportFileName(bundle: AgentBundle): string {
  const safeName =
    bundle.agent.name
      .replace(/[^a-zA-Z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'agent';
  const version = bundle.source?.version;
  return version
    ? `${safeName}.v${version}.agent.json`
    : `${safeName}.agent.json`;
}

/** Download an exported agent bundle as a `.agent.json` file. */
export function downloadAgentBundle(bundle: AgentBundle): void {
  downloadJsonText(agentExportFileName(bundle), agentBundleJson(bundle));
}

type ParsedBundle =
  { ok: true; bundle: AgentBundle } | { ok: false; error: string };

function validateBundle(raw: unknown): ParsedBundle {
  const parsed = parseAgentBundle(raw);
  if (!parsed.ok) {
    return {
      ok: false,
      error: `Invalid agent file: ${summarizeValidationErrors(parsed.errors)}`,
    };
  }
  return { ok: true, bundle: parsed.bundle };
}

/** Parse pasted/loaded text into a bundle, or a single readable error message. */
export function parseAgentBundleText(text: string): ParsedBundle {
  const read = parseJsonText(text);
  return read.ok ? validateBundle(read.raw) : read;
}

/** Read a `.json` file into a validated bundle, or an error message for the dialog. */
export async function readAgentBundleFile(file: File): Promise<ParsedBundle> {
  const read = await readJsonFile(file);
  return read.ok ? validateBundle(read.raw) : read;
}
